import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ThesisBindingType, ThesisDegreeLevel } from "@/lib/api/bindery";
import { BINDING_TYPE_LABELS } from "../binderyFormatters";
import { ThesisPriceAmountField } from "./ThesisPriceAmountField";
import { ThesisPriceExample } from "./ThesisPriceExample";
import { PRICE_FIELDS, type PriceDraft } from "./thesis-pricing-fields";

interface ThesisPriceRuleEditorProps {
  degree: ThesisDegreeLevel;
  binding: ThesisBindingType;
  draft: PriceDraft;
  saving: boolean;
  locked: boolean;
  onChange: (patch: Partial<PriceDraft>) => void;
  onSave: () => void;
}

export function ThesisPriceRuleEditor({
  degree,
  binding,
  draft,
  saving,
  locked,
  onChange,
  onSave,
}: ThesisPriceRuleEditorProps) {
  const bindingLabel = BINDING_TYPE_LABELS[binding];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{bindingLabel}</CardTitle>
        <p className="text-sm text-slate-500">
          Used when a student chooses {bindingLabel.toLowerCase()} for this degree.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          {PRICE_FIELDS.map((field) => (
            <ThesisPriceAmountField
              key={field.key}
              id={`${degree}-${binding}-${field.key}`}
              label={field.label}
              hint={field.hint}
              value={draft[field.key]}
              onChange={(value) => onChange({ [field.key]: value })}
            />
          ))}
        </div>
        <ThesisPriceExample draft={draft} />
        <div className="flex justify-end">
          <Button type="button" onClick={onSave} disabled={saving || locked}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save {bindingLabel.toLowerCase()}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
